/**
 * 用户信息状态管理
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserInfo } from '@/api'

export const useUserStore = defineStore('user', () => {
  // ============ 状态 ============
  const info = ref<UserInfo | null>(null)
  const _isLoggedIn = ref(false)

  // ============ 计算属性 ============
  const isLoggedIn = computed(() => _isLoggedIn.value || !!info.value)

  const role = computed(() => {
    if (!info.value) return 'guest'
    return info.value.role
  })

  const userAuthority = computed(() => {
    if (!info.value?.authority) return []
    return info.value.authority.split(',').map(Number).filter(num => !isNaN(num))
  })

  const userName = computed(() => info.value?.name || '未登录')

  const allRoles = computed(() => {
    if (!info.value) return ['guest']
    const baseRole = info.value.role
    return [baseRole, 'guest']
  })

  // ============ 方法 ============
  /**
   * 设置用户信息
   * @param userData 用户信息
   */
  const setUserInfo = (userData: UserInfo) => {
    info.value = userData
    _isLoggedIn.value = true
    // 统一使用 sessionStorage
    sessionStorage.setItem('userInfo', JSON.stringify(userData))
    sessionStorage.setItem('isLoggedIn', 'true')
    sessionStorage.setItem('loginTime', Date.now().toString())
  }

  /**
   * 清除用户状态（登出时调用）
   */
  const clearUserInfo = () => {
    info.value = null
    _isLoggedIn.value = false
    // 统一使用 sessionStorage
    sessionStorage.removeItem('userInfo')
    sessionStorage.removeItem('isLoggedIn')
    sessionStorage.removeItem('loginTime')
    sessionStorage.removeItem('redirectPath')
    sessionStorage.removeItem('cachedViews')
    sessionStorage.removeItem('visitedViews')
  }

  /**
   * 更新用户状态
   * @param partialInfo 用户状态
   */
  const updateUserInfo = (partialInfo: Partial<UserInfo>) => {
    if (info.value) {
      info.value = { ...info.value, ...partialInfo }
      sessionStorage.setItem('userInfo', JSON.stringify(info.value))
    }
  }

  /**
   * 从本地存储恢复用户状态
   */
  const restoreLoginState = () => {
    try {
      const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true'
      const userInfoStr = sessionStorage.getItem('userInfo')
      const loginTime = sessionStorage.getItem('loginTime')
      if (isLoggedIn && userInfoStr) {
        // 检查登录是否过期（7天）
        if (loginTime) {
          const loginTimestamp = parseInt(loginTime)
          const sevenDays = 7 * 24 * 60 * 60 * 1000
          if (Date.now() - loginTimestamp > sevenDays) {
            clearUserInfo()
            return
          }
        }
        const userInfo: UserInfo = JSON.parse(userInfoStr)
        info.value = userInfo
        _isLoggedIn.value = true
      }
    } catch (error) {
      clearUserInfo()
    }
  }

  return {
    // 状态
    info,
    // 计算属性
    isLoggedIn,
    role,
    userAuthority,
    userName,
    allRoles,
    userInfo: computed(() => info.value),
    // 方法
    setUserInfo,
    clearUserInfo,
    updateUserInfo,
    restoreLoginState
  }
})
